import store from 'javascripts/store.js';
import {action, computed, observable} from 'mobx';
import {allParams} from 'javascripts/utils/params';
import {amountWithPrecisionAsRequired} from 'javascripts/utils/numberFormatter';
import {experiments} from 'javascripts/utils/optimizeleyInfo';
import {pauseForSpecs} from 'javascripts/utils/testing';

export default class {
  @observable amount = 0;
  @observable amountInputShouldFocus = false;
  @observable analyticsUuid;
  @observable authenticityToken;
  @observable billingAddress = '';
  @observable billingCity = '';
  @observable billingCountry = '';
  @observable billingZipcode = '';
  @observable campaignId = '';
  @observable campaignName = '';
  @observable campaignPath = '';
  @observable campaignSlug = '';
  @observable campaignType = '';
  @observable campaignerName = '';
  @observable campaignerProfileImageTag = '';
  @observable cardElementCompleted = false;
  @observable cardElementStep1Error = '';
  @observable cardExpirationMonth = '';
  @observable cardExpirationYear = '';
  @observable clickedPaypalSubmit = false;
  @observable comment = '';
  @observable costPerPerson = '';
  @observable country = 'us';
  @observable creditCardStep = 1;
  @observable currentPaymentMethod = '';
  @observable customDefaultAmounts = {};
  @observable customDonateButtonText = '';
  @observable disableEditAmount = false;
  @observable disableFormSubmit = true;
  @observable disableHeader = false;
  @observable disableMembershipCheck = false;
  @observable disablePaypal = false;
  @observable disablePhoneNumber = false;
  @observable displayName = '';
  @observable donationFormId = '';
  @observable disableRecaptcha = false;
  @observable email = '';
  @observable endpointPaths = '';
  @observable existingMembershipCountry;
  @observable claimedGiftAid = false;
  @observable host = '';
  @observable isAlreadySpringMember = false;
  @observable isAnonymous = false;
  @observable isBpsDonateForm = false;
  @observable isFormMounted = false;
  @observable isIho = false;
  @observable isOrganic = undefined;
  @observable isReferral = false;
  @observable isSubscription = false;
  @observable isUserLoggedIn = false;
  @observable isValidEmail = true;
  @observable last4 = '';
  @observable managementMode = false;
  @observable membershipAlreadyExistsMessageDisabled = false;
  @observable minAmount = '';
  @observable name = '';
  @observable onlyAcceptsIho = false;
  @observable paymentGatewayToken = '';
  @observable paymentIntentId = '';
  @observable paymentIntentSecret = '';
  @observable setupIntentId = '';
  @observable setupIntentSecret = '';
  @observable paymentRequestButtonError = '';
  @observable paymentTab = '';
  @observable phoneNumberConsentGranted = false;
  @observable productVariant = '';
  @observable isIndividualAccount = false;
  @observable optimizelyParams = {};
  @observable plaidAccountId = '';
  @observable plaidEnv = '';
  @observable plaidToken = '';
  @observable queryParams = {};
  @observable recaptchaRef;
  @observable recaptchaV2Token;
  @observable referralSource = {};
  @observable referer = '';
  @observable showAchPopoutForm = false;
  @observable showAmountInputExperiment = false;
  @observable showDonateButtons = false;
  @observable showSubscriptionOption = false;
  @observable simulateStripeWebhooks = false;
  @observable step = 'amount';
  @observable showOtherAmountInput = false;
  @observable submitHandler;
  @observable subscriptionMinAmount = '';
  @observable supportsApplePay = false;
  @observable supportsPaymentRequestButton = false;
  @observable surname = '';
  @observable title = '';
  @observable titleOptions = [];
  @observable userCampaignDonationFullName = '';
  @observable customNsIntegrationCodeFor1xDonations = '';
  @observable customNsIntegrationCodeFor1xDonationsGte10k = '';
  @observable customXeroIntegrationCodeFor1xDonations = '';
  @observable customXeroIntegrationCodeFor1xDonationsGte8k = '';

  phoneNumber = new class {
    @observable displayValue = '';
    @observable existingValue = '';
    @observable hiddenFieldValue = '';
    @observable selectedCountry = '';
  }();

  @action setProductMonthlyOnly = () => {
    this.isSubscription = true;
    this.showSubscriptionOption = false;
  };

  @action setProductOneTimeOnly = () => {
    this.isSubscription = false;
    this.showSubscriptionOption = false;
  };

  @action setProductOneTimeAndMonthly = () => {
    this.showSubscriptionOption = true;
  };

  @action setProductIho = () => {
    this.isSubscription = false;
    this.showSubscriptionOption = false;
    this.isIho = true;
    this.onlyAcceptsIho = true;
  };

  @action setCustomDefaultAmount = () => {
    if (this.customDefaultAmountsPresent) {
      this.amount = this.isSubscription ? this.customDefaultAmounts.monthly : this.customDefaultAmounts.oneTime;
    }
  }

  @action setNameAndSurnameFromPayerName = (payerName) => {
    const split = payerName.trim().split(' ');
    const surname = split.pop(' ');
    const name = split.join(' ');

    this.name = name;
    this.surname = surname;
  }

  @action handleRecaptchaSuccess = (token) => {
    this.recaptchaSuccessCallback(token);
    delete this.recaptchaSuccessCallback;
    this.recaptchaRef.reset();
  }

  @action setUpHandlerForRecaptchaModalBeingDismissed = () => {
    const iframe = document.querySelector('iframe[src*="www.google.com/recaptcha"][src*="bframe"]');
    const iframeContainer = iframe.parentNode.parentNode;
    const observer = new MutationObserver((_, observer) => {
      if(iframeContainer && iframeContainer.style.visibility === 'hidden') {
        store.donateForm.disableFormSubmit = false;
        observer.disconnect();
      }
    });
    observer.observe(iframeContainer, { attributes : true, attributeFilter : ['style'] });
  }

  @action doRecaptchaV2Check = (recaptchaSuccessCallback) => {
    if(this.disableRecaptcha) {
      recaptchaSuccessCallback('disabled');
      return;
    };

    this.recaptchaSuccessCallback = recaptchaSuccessCallback;
    this.recaptchaRef.execute();
    this.setUpHandlerForRecaptchaModalBeingDismissed();
  }

  @action createOrUpdatePaymentIntent = (successCallback) => {
    this.doRecaptchaV2Check((token) => {
      this.recaptchaV2Token = token;

      if(this.paymentIntentId) {
        this.updatePaymentIntent(successCallback);
      } else {
        this.createPaymentIntent(successCallback);
      }
    });
  }

  @action createSetupIntent = (successCallback) => {
    this.doRecaptchaV2Check((token) => {
      this.recaptchaV2Token = token;

      store.donateForm.disableFormSubmit = true;

      $.ajax({
        url: '/api/v1/stripe/setup_intents',
        method: 'POST',
        data: this.setupIntentParams,
        success: (response) => {
          this.setupIntentId = response.id;
          this.setupIntentSecret = response.setup_intent_secret;
          this.disableFormSubmit = false;

          if(successCallback) successCallback();
        },
      });
    });
  }

  @action createPaymentIntent = (successCallback) => {
    $.post(
      '/api/v1/stripe/payment_intents',
      this.paymentIntentParams,
      (response) => { this.handlePaymentIntentSuccess(response, successCallback); },
    );
  }

  @action updatePaymentIntent = (successCallback) => {
    $.ajax({
      url: `/api/v1/stripe/payment_intents/${this.paymentIntentId}`,
      method: 'PUT',
      data: this.paymentIntentParams,
      success: (response) => { this.handlePaymentIntentSuccess(response, successCallback); },
    });
  }

  @action handlePaymentIntentSuccess = (response, callback) => {
    this.paymentIntentId = response.id;
    this.paymentIntentSecret = response.payment_intent_secret;

    pauseForSpecs(() => {
      this.disableFormSubmit = false;
    });

    if(callback) callback();
  }

  @action handlePaymentRequestError = (error) => {
    this.paymentRequestButtonError = error;
    this.disableFormSubmit = false;
  }

  @action createOrUpdateDonationForm(paymentGateway, successCallback, errorCallback) {
    if(this.donationFormId) {
      this.updateDonationForm(paymentGateway, successCallback, errorCallback);
    } else {
      this.createDonationForm(paymentGateway, successCallback, errorCallback);
    }
  }

  @action createDonationForm(paymentGateway, successCallback, errorCallback) {
    this.paymentGateway = paymentGateway;

    $.ajax({
      url: store.donateForm.endpointPaths[this.paymentGateway],
      method: 'POST',
      data: this.donationFormParams,
      success: (response) => {
        this.donationFormId = response.donationFormId;

        if(this.paymentGateway == 'stripe' && this.simulateStripeWebhooks) {
          $.post('/api/v1/stripe/simulated_stripe_events', {
            country: this.country,
            payment_id: this.paymentIntentId || this.setupIntentId,
          });
        }

        if(successCallback) successCallback(response);
      },
      error: (response) => {
        if(errorCallback) errorCallback(response.responseJSON);
      },
    });
  }

  @action updateDonationForm(paymentGateway, successCallback, errorCallback) {
    this.paymentGateway = paymentGateway;

    $.ajax({
      url: `${store.donateForm.endpointPaths[this.paymentGateway]}/${this.donationFormId}`,
      method: 'PATCH',
      data: this.donationFormParams,
      success: (response) => {
        if(successCallback) successCallback(response);
      },
      error: (response) => {
        if(errorCallback) errorCallback(response.responseJSON);
      },
    });
  }

  @computed get donationFormParams() {
    if(this.managementMode) {
      return {
        account_id: this.plaidAccountId,
        authenticity_token: this.authenticityToken,
        exp_month: this.cardExpirationMonth,
        exp_year: this.cardExpirationYear,
        gift_aid: this.giftAidParams,
        last4: this.last4,
        payment_token: this.paymentGatewayToken || this.plaidToken,
        setup_intent_id: this.setupIntentId,
        zip_code: this.billingZipcode,
      };
    }

    const params = {
      donation_form: {
        amount: this.amount,
        anonymous: this.isAnonymous,
        comment: this.comment,
        display_name: this.displayName,
        email: this.email,
        name: this.name,
        payment_gateway: this.paymentGateway,
        payment_gateway_token: this.paymentGatewayToken,
        payment_monthly_subscription: this.isSubscription,
        surname: this.surname,
        title: this.title,
        campaign_id: this.campaignId,
        analytics_uuid: this.analyticsUuid,
        setup_intent_id: this.setupIntentId,
        metadata: {
          experiments: experiments(),
          phone_number: this.phoneNumber.existingValue || this.phoneNumber.hiddenFieldValue,
          phone_number_consent_granted: this.phoneNumberConsentGranted,
          plaid_account_id: this.plaidAccountId,
          plaid_public_token: this.plaidToken,
          referer: this.referer,
          url_params: allParams(),
        },
      },
    };

    if(this.paymentGateway === 'stripe') {
      params['donation_form']['payment_intent_id'] = this.paymentIntentId;
    }

    if(this.acceptsIho) {
      params['donation_form']['iho_attributes'] = store.iho.donationFormParams;
    }

    if(this.isBpsDonateForm) {
      params['donation_form']['next_payment_datetime'] = store.bps.paymentDate;
      params['donation_form']['amount'] = store.bps.subscriptionAmount;
    }

    if(this.isReferral) {
      params['donation_form']['metadata']['referral'] = {
        source_id: this.referralSource.sourceId,
        source_mechanism: this.referralSource.sourceMechanism,
        source_type: this.referralSource.sourceType,
        source_user_id: this.referralSource.sourceUserId,
      };
    }

    if(this.customNsIntegrationCodeFor1xDonations) {
      params['donation_form']['metadata']['custom_ns_integration_code_for_1x_donations'] = this.customNsIntegrationCodeFor1xDonations;
      params['donation_form']['metadata']['custom_ns_integration_code_for_1x_donations_gte_10k'] = this.customNsIntegrationCodeFor1xDonationsGte10k;
    }

    if(this.customXeroIntegrationCodeFor1xDonations) {
      params['donation_form']['metadata']['custom_xero_integration_code_for_1x_donations'] = this.customXeroIntegrationCodeFor1xDonations;
      params['donation_form']['metadata']['custom_xero_integration_code_for_1x_donations_gte_8k'] = this.customXeroIntegrationCodeFor1xDonationsGte8k;
    }

    params['donation_form']['gift_aid'] = this.giftAidParams;

    return params;
  }

  @computed get giftAidParams() {
    if(!this.claimedGiftAid) return {};

    return {
      house_name_or_number: this.billingAddress,
      name: this.name,
      postal_code: this.billingZipcode,
      surname: this.surname,
      title: this.title,
    };
  }

  @action handleCardError = (error) => {
    this.cardElementStep1Error = error;
    this.creditCardStep = 1;
    this.disableFormSubmit = false;
  }

  @computed get setupIntentParams() {
    return {
      country: this.country,
      setup_intent: {
        email: this.email,
      },
      recaptcha: {
        token: this.recaptchaV2Token,
      },
    };
  }

  @computed get paymentIntentParams() {
    return {
      country: this.country,
      payment_intent: {
        email: this.email,
        amount: this.amount,
        currency: this.currencyName,
        metadata: {
          analytics_id: window.analytics.user().anonymousId(),
          safe_product_feature_used: this.safeProductFeatureUsed,
        },
      },
      recaptcha: {
        token: this.recaptchaV2Token,
      },
    };
  }

  @computed get paymentMethodData() {
    return {
      payment_method_data: {
        billing_details: {
          address: {
            ...(this.shouldIncludeZipcode && { postal_code: this.billingZipcode }),
            city: this.billingCity,
            country: this.defaultOrSelectedBillingCountry,
            line1: this.billingAddress,
          },
          email: this.email,
          name: this.fullName,
        },
      },
    };
  }

  @computed get shouldIncludeZipcode() {
    return (
      store.donateForm.defaultOrSelectedBillingCountry === 'US' ||
      store.donateForm.defaultOrSelectedBillingCountry === 'GB'
    );
  }

  @computed get formId() {
    return `${this.campaignSlug}_form`;
  }

  @computed get acceptsIho() {
    return this.isIho && !this.isSubscription;
  }

  @computed get defaultOrSelectedBillingCountry() {
    if(this.billingCountry) return this.billingCountry;

    return {
      us: 'US',
      uk: 'GB',
    }[this.country];
  }

  @computed get defaultPaymentTab() {
    return 'stripe';
  }

  @computed get currencyUnit() {
    return {
      us: '$',
      uk: '£',
    }[this.country];
  }

  @computed get currencyName() {
    return {
      us: 'usd',
      uk: 'gbp',
    }[this.country];
  }

  @computed get locale() {
    return {
      us: 'en',
      uk: 'en-GB',
    }[this.country];
  }

  @computed get giftAidAmount() {
    return amountWithPrecisionAsRequired(this.amount * .25);
  }

  @computed get donateButtonText() {
    if(this.customDonateButtonText) return this.customDonateButtonText;
    return `Donate ${this.currencyUnit}${amountWithPrecisionAsRequired(this.amount)}${this.isSubscription ? ' monthly' : ''}`;
  }

  @computed get showPhoneNumberField() {
    return (
      this.isSubscription &&
      (!this.isUserLoggedIn || this.isUserLoggedIn && !this.phoneNumber.existingValue && !this.disablePhoneNumber)
    );
  }

  @computed get showSpringMembershipAlreadyExistsMessage() {
    return this.isSubscription &&
      this.isAlreadySpringMember &&
        !this.membershipAlreadyExistsMessageDisabled &&
        !this.disableMembershipCheck;
  }

  @computed get showUsToUkConversionMessage() {
    return this.country.toLowerCase() == 'uk' &&
      this.existingMembershipCountry.toLowerCase() == 'us';
  }

  @computed get isPaypalAvailable() {
    if(this.country == 'uk') return false;
    if(this.disablePaypal) return false;
    return true;
  }

  @computed get buttonAmounts() {
    if(this.isSubscription) {
      if(this.campaignSlug == 'default') {
        return {
          values: [5, 25, 45, 65],
          highlight: 25,
        };
      } else {
        return {
          us: {
            values: [
              this.optimizelyParams.monthly1 || this.queryParams.monthly1 || 20,
              this.optimizelyParams.monthly2 || this.queryParams.monthly2 || 40,
              this.optimizelyParams.monthly3 || this.queryParams.monthly3 || 60,
              this.optimizelyParams.monthly4 || this.queryParams.monthly4 || 80,
            ],
            highlight: this.optimizelyParams.monthlyHighlight || this.queryParams.monthlyHighlight || 40,
          },
          uk: {
            values: [
              this.optimizelyParams.monthly1 || this.queryParams.monthly1 || 25,
              this.optimizelyParams.monthly2 || this.queryParams.monthly2 || 50,
              this.optimizelyParams.monthly3 || this.queryParams.monthly3 || 75,
              this.optimizelyParams.monthly4 || this.queryParams.monthly4 || 100,
            ],
            highlight: this.optimizelyParams.monthlyHighlight || this.queryParams.monthlyHighlight || 50,
          },
        }[this.country];
      }
    } else {
      return {
        us: {
          values: [
            this.optimizelyParams.oneTime1 || this.queryParams.oneTime1 || 50,
            this.optimizelyParams.oneTime2 || this.queryParams.oneTime2 || 100,
            this.optimizelyParams.oneTime3 || this.queryParams.oneTime3 || 150,
            this.optimizelyParams.oneTime4 || this.queryParams.oneTime4 || 200,
          ],
          highlight: this.optimizelyParams.oneTimeHighlight || this.queryParams.oneTimeHighlight || 100,
        },
        uk: {
          values: [
            this.optimizelyParams.oneTime1 || this.queryParams.oneTime1 || 25,
            this.optimizelyParams.oneTime2 || this.queryParams.oneTime2 || 50,
            this.optimizelyParams.oneTime3 || this.queryParams.oneTime3 || 75,
            this.optimizelyParams.oneTime4 || this.queryParams.oneTime4 || 100,
          ],
          highlight: this.optimizelyParams.oneTimeHighlight || this.queryParams.oneTimeHighlight || 50,
        },
      }[this.country];
    }
  }

  @computed get fullName() {
    return `${this.name} ${this.surname}`;
  }

  @computed get isSystemCampaign() {
    return this.campaignType == 'system';
  }

  @computed get safeProductFeatureUsed() {
    return store.iho.honoreeInfoIsPresent ||
      !this.isSystemCampaign ||
      !this.isOrganic;
  }

  @computed get customDefaultAmountsPresent() {
    return store.donateForm.customDefaultAmounts &&
           store.donateForm.customDefaultAmounts.oneTime &&
           store.donateForm.customDefaultAmounts.monthly;
  }
}
